import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { company } from '../data/content';
import { navSections, quickLinks } from '../data/navigation';
import { PhoneIcon, ArrowRightIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState('top');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveId(visible.target.id);
    }, {
      rootMargin: '-40% 0px -50% 0px',
      threshold: [0.05, 0.25, 0.5]
    });
    navSections.forEach(section => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  const compactLinks = navSections.filter(section => quickLinks.includes(section.id));
  return <header className="sticky top-0 z-50 w-full border-b border-navy-800/60 bg-navy-900/95 backdrop-blur" data-id="element-313" data-mp="2hovhttb1615w">
      <div className="mx-auto flex max-w-content items-center gap-4 px-5 py-3 lg:px-8" data-id="element-314" data-mp="3fasw1eidnkc1">
        <a href="#top" className="flex items-center gap-3" onClick={() => setIsOpen(false)} data-id="element-315" data-mp="3adkbx8do5685">
          <img src={company.logo} alt="" className="h-11 w-11 object-contain" data-id="element-316" data-mp="35khgsvt8topu" />
          <span className="leading-tight" data-id="element-317" data-mp="30mzcjiq67xo6">
            <span className="block font-display text-base font-bold tracking-tight text-white" data-id="element-318" data-mp="30m65egnav65i">
              A&amp;J Pool Plaster
            </span>
            <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-sky-300" data-id="element-319" data-mp="3talgis3b94bk">
              Licensed &amp; Insured
            </span>
          </span>
        </a>

        <nav aria-label="Primary" className="ml-auto hidden items-center gap-7 xl:flex" data-id="element-320" data-mp="1wcehmam9pb07">
          {compactLinks.map(link => <a key={link.id} href={`#${link.id}`} aria-current={activeId === link.id ? 'true' : undefined} className={`text-sm font-medium transition-colors duration-150 ease-out hover:text-white ${activeId === link.id ? 'text-sky-300' : 'text-navy-100'}`} data-id="element-321" data-mp="1hmqgpai00ed0">
              {link.label}
            </a>)}
        </nav>

        <div className="ml-auto flex items-center gap-2.5 xl:ml-0" data-id="element-322" data-mp="yqqkfnhzmonm">
          <a href={company.phoneHref} className="flex items-center gap-2 rounded-md bg-sky-400 px-4 py-2.5 text-sm font-bold text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-323" data-mp="13p1u9y8a8z5u">
            <PhoneIcon className="h-4 w-4" data-id="element-324" data-mp="mck54w7i0wpw" />
            <span className="hidden sm:inline" data-id="element-325" data-mp="hgcvasbzf12o">{company.phoneDisplay}</span>
            <span className="sm:hidden" data-id="element-326" data-mp="w4hjucxpu6td">Call</span>
          </a>

          <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-controls="section-menu" aria-label={isOpen ? 'Close sections menu' : 'Open sections menu'} title="Sections" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-navy-600 text-white transition-colors duration-150 ease-out hover:border-sky-300 hover:text-sky-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-327" data-mp="w5slo77lp4hu">
            <span className="flex h-4 w-[18px] flex-col justify-between" aria-hidden="true" data-id="element-328" data-mp="3gwt0sa2jjfww">
              <motion.span animate={{
              rotate: isOpen ? 45 : 0,
              y: isOpen ? 7 : 0
            }} transition={{
              duration: 0.18,
              ease
            }} className="block h-[2px] w-full origin-center rounded-full bg-current" data-id="element-329" data-mp="3bxgeprjhucvd" />
              <motion.span animate={{
              opacity: isOpen ? 0 : 1
            }} transition={{
              duration: 0.12,
              ease
            }} className="block h-[2px] w-full rounded-full bg-current" data-id="element-330" data-mp="2cwlcchaud6al" />
              <motion.span animate={{
              rotate: isOpen ? -45 : 0,
              y: isOpen ? -7 : 0
            }} transition={{
              duration: 0.18,
              ease
            }} className="block h-[2px] w-full origin-center rounded-full bg-current" data-id="element-331" data-mp="1k9h3u2cji9u2" />
            </span>
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-332" data-mp="1y1ov5el218ld">
        {isOpen && <motion.div id="section-menu" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="overflow-hidden border-t border-navy-700 bg-navy-900" data-id="element-333" data-mp="11gmxtxww9s1z">
            <nav aria-label="All sections" className="mx-auto max-h-[70vh] max-w-content overflow-y-auto px-5 py-5 lg:px-8" data-id="element-334" data-mp="sykv8divt7rq">
              <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3" data-id="element-335" data-mp="jz7klhj0ud5f">
                {navSections.map((section, index) => {
              const isActive = activeId === section.id;
              return <li key={section.id} data-id="element-336" data-mp="a8v35y6i703i" data-mpi={index}>
                      <a href={`#${section.id}`} onClick={() => setIsOpen(false)} aria-current={isActive ? 'true' : undefined} className={`group flex items-start gap-3 rounded-md px-3.5 py-3 transition-colors duration-150 ease-out ${isActive ? 'bg-navy-700' : 'hover:bg-navy-800'}`} data-id="element-337" data-mp="5atk3d92v0s1">
                        <span className={`mt-0.5 font-display text-[11px] font-bold tabular-nums ${isActive ? 'text-sky-300' : 'text-navy-300'}`} aria-hidden="true" data-id="element-338" data-mp="3drskuh56st3n">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-339" data-mp="epiewmodo3i">
                          <span className={`block font-display text-[15px] font-bold leading-tight ${isActive ? 'text-sky-200' : 'text-white'}`} data-id="element-340" data-mp="5dtqfjxv5fs9">
                            {section.label}
                          </span>
                          <span className="mt-0.5 block text-[13px] leading-snug text-navy-200" data-id="element-341" data-mp="jbief0n8931j">
                            {section.description}
                          </span>
                        </span>
                        <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-navy-400 transition-colors duration-150 ease-out group-hover:text-sky-300" data-id="element-342" data-mp="28sckowz1yax6" />
                      </a>
                    </li>;
            })}
              </ul>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}